// src/components/OfflineBanner.jsx
//
// Sticks a subtle banner at the top of the viewport when the browser
// reports `navigator.onLine === false`. When writes fail while offline
// they land in the mutation queue; the banner shows the pending count
// and a retry button. When back online, a short "synced" confirmation
// surfaces if anything was drained.

import React, { useEffect, useState } from 'react';
import { Alert, Button, Slide, Box, Typography } from '@mui/material';
import { CloudOff as OfflineIcon, CloudDone as CloudDoneIcon } from '@mui/icons-material';
import useOfflineQueue from '../hooks/useOfflineQueue';

const OfflineBanner = () => {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [justSynced, setJustSynced] = useState(false);
  const { pendingCount, failedCount, replay, clearFailed } = useOfflineQueue();

  useEffect(() => {
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
    };
  }, []);

  // Flash a "synced" confirmation when the queue drains after being
  // non-empty. Tracks the previous pendingCount to detect the transition
  // from "had work" to "all clear" without flashing on first mount.
  const prevPendingRef = React.useRef(pendingCount);
  useEffect(() => {
    if (prevPendingRef.current > 0 && pendingCount === 0 && online) {
      setJustSynced(true);
      const t = setTimeout(() => setJustSynced(false), 3000);
      prevPendingRef.current = pendingCount;
      return () => clearTimeout(t);
    }
    prevPendingRef.current = pendingCount;
    return undefined;
  }, [pendingCount, online]);

  const showOffline = !online;
  const showFailed = online && failedCount > 0;
  const showSynced = online && justSynced && !failedCount;
  const visible = showOffline || showFailed || showSynced;

  return (
    <Slide in={visible} direction="down" mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 'env(safe-area-inset-top, 0px)',
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.snackbar,
          px: { xs: 1, md: 2 },
          pointerEvents: 'none',
        }}
      >
        {showOffline && (
          <Alert
            icon={<OfflineIcon />}
            severity="warning"
            variant="filled"
            sx={{
              mt: 1,
              mx: 'auto',
              maxWidth: 560,
              borderRadius: 2,
              boxShadow: 4,
              pointerEvents: 'auto',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              You're offline. The portal is showing cached data.
              {pendingCount > 0
                ? ` ${pendingCount} change${pendingCount === 1 ? '' : 's'} queued — they'll sync automatically when the connection returns.`
                : ' Changes you make will sync when the connection returns.'}
            </Typography>
          </Alert>
        )}
        {showFailed && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              mt: 1,
              mx: 'auto',
              maxWidth: 560,
              borderRadius: 2,
              boxShadow: 4,
              pointerEvents: 'auto',
              alignItems: 'center',
            }}
            action={(
              <>
                <Button color="inherit" size="small" onClick={() => replay()} sx={{ fontWeight: 700 }}>
                  Retry
                </Button>
                <Button color="inherit" size="small" onClick={() => clearFailed()}>
                  Dismiss
                </Button>
              </>
            )}
          >
            {failedCount} queued change{failedCount === 1 ? '' : 's'} failed to sync.
          </Alert>
        )}
        {showSynced && (
          <Alert
            icon={<CloudDoneIcon />}
            severity="success"
            variant="filled"
            sx={{
              mt: 1,
              mx: 'auto',
              maxWidth: 560,
              borderRadius: 2,
              boxShadow: 4,
              pointerEvents: 'auto',
              alignItems: 'center',
            }}
          >
            Changes synced successfully.
          </Alert>
        )}
      </Box>
    </Slide>
  );
};

export default OfflineBanner;
